import {
  type DefaultMantineColor,
  Drawer,
  JsonInput,
  type MantineColorsTuple,
  NumberInput,
  Overlay,
} from "@mantine/core";
import {
  ActionIcon,
  Alert,
  Button,
  createTheme,
  Loader,
  Modal,
  Notification,
  PasswordInput,
  TextInput,
  ThemeIcon,
} from "@mantine/core";

import classes from "./mantine.module.css";
import "./mantine.css";

export type CustomColors = "primary" | DefaultMantineColor;

declare module "@mantine/core" {
  export interface MantineThemeColorsOverride {
    colors: Record<CustomColors, MantineColorsTuple>;
  }
}

const stone: MantineColorsTuple = [
  "#fafaf9",
  "#f5f5f4",
  "#e7e5e4",
  "#d6d3d1",
  "#a8a29e",
  "#78716c",
  "#57534e",
  "#44403c",
  "#292524",
  "#1c1917",
  "#0c0a09",
];

export const THEME = createTheme({
  autoContrast: true,
  cursorType: "pointer",
  colors: {
    stone,
    primary: stone,
  },
  primaryColor: "primary",
  defaultRadius: "lg",
  fontFamily:
    "Manrope Variable, Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, " +
    "Arial, sans-serif",
  fontFamilyMonospace:
    "JetBrains Mono Variable, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, " +
    "Liberation Mono, Courier New, monospace",
  headings: {
    fontFamily:
      "Manrope Variable, Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, " +
      "Arial, sans-serif",
  },
  focusClassName: cn("mantine-focus-auto", classes.focus),
  components: {
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        variant: "outline",
      },
    }),
    Alert: Alert.extend({
      styles: {
        title: {
          fontWeight: 800,
        },
      },
    }),
    Badge: Badge.extend({
      classNames: {
        label: classes.badgeLabel,
      },
    }),
    Button: Button.extend({
      defaultProps: {
        variant: "outline",
      },
      classNames: {
        root: classes.button,
      },
    }),
    Drawer: Drawer.extend({
      styles: ({ headings: { sizes, ...style } }) => ({
        title: {
          ...sizes.h4,
          ...style,
        },
      }),
    }),
    Group: Group.extend({
      defaultProps: {
        wrap: "nowrap",
      },
    }),
    Loader: Loader.extend({
      defaultProps: {
        size: "sm",
        color: "primary.5",
      },
    }),
    Modal: Modal.extend({
      styles: ({ headings: { sizes, ...style } }) => ({
        header: {
          alignItems: "start",
        },
        title: {
          ...sizes.h4,
          ...style,
        },
      }),
    }),
    Overlay: Overlay.extend({
      defaultProps: {
        blur: 2,
      },
    }),
    JsonInput: JsonInput.extend({
      defaultProps: {
        variant: "filled",
      },
      classNames: {
        input: classes.input,
      },
    }),
    Notification: Notification.extend({
      styles: ({ lineHeights, spacing }) => ({
        title: {
          marginBottom: 0,
        },
        description: {
          lineHeight: lineHeights.xs,
        },
        icon: {
          backgroundColor: "transparent",
          color: "var(--notification-color)",
          marginInlineEnd: spacing.xs,
        },
      }),
    }),
    NumberInput: NumberInput.extend({
      defaultProps: {
        variant: "filled",
      },
      classNames: {
        input: classes.input,
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        variant: "filled",
      },
      classNames: {
        input: classes.input,
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        variant: "filled",
      },
      classNames: {
        input: classes.input,
      },
    }),
    ThemeIcon: ThemeIcon.extend({
      defaultProps: {
        variant: "default",
      },
    }),
    PasswordInput: PasswordInput.extend({
      defaultProps: {
        variant: "filled",
      },
      classNames: {
        input: classes.input,
      },
    }),
  },
});
